import React, { useMemo } from 'react';
import './FlickerPreview.css';
import {
  buildFlickerPreview,
  toPolylinePoints,
  FLICKER_DETECT_THRESHOLD_PERCENT,
  DEFLICKER_WINDOW_DEFAULT,
} from '../../lib/flickerPreview';
import { OPERATIONS, DEFLICKER_STRENGTHS } from '../../lib/operationCopy';

// The flicker proof graph inside the "Fix flicker" card (#405).
//
// Flicker is invisible in a thumbnail, so until now the only way to check the
// fix was to pay for a render and watch it. This draws the answer from data the
// analyzer already stored (#402): no ffmpeg, no worker call, no credits.
//
// It is a PREDICTION OF MEAN FRAME BRIGHTNESS, not a preview of the output —
// every string below is written to say that, and none of them may be softened
// into implying a rendered A/B. The math, and the reason the chart sometimes
// switches to a different quantity entirely, live in lib/flickerPreview.js.

// SVG user units. The viewBox is fixed and the element is stretched to the
// card's width with preserveAspectRatio="none", so the chart is responsive
// without a resize observer. Strokes carry vector-effect="non-scaling-stroke"
// so that horizontal stretch cannot thin the lines into invisibility.
const W = 600;
const H = 132;

// The operation's name comes from lib/operationCopy.js (#379), the single
// naming source — never re-typed here. (The prose strings further down still
// carry the literal from before that rule existed; renaming the operation would
// have to sweep them, which is not this change's job.)
const DEFLICKER_NAME = OPERATIONS.deflicker.displayName;

// The residual chart's second series. "(estimated)" is load-bearing and is part
// of the label rather than the caption, because the legend is the one place a
// skimming reader looks: this line is computed from the analysis, and no part
// of this card may read as a rendered before/after.
//
// The STRENGTH is named here for a second reason: the after-line is the only
// thing the control moves, and an unlabelled line invites the reader to compare
// it against a remembered earlier position of the control. Naming it also makes
// the "never label window A's series as window B" rule visible on screen rather
// than only in the data layer — the name is taken from the same `frameWindow`
// the series was looked up by.
const afterSeriesLabel = (strengthLabel) =>
  `After ${DEFLICKER_NAME} — ${strengthLabel} (estimated)`;

// The chosen tile's words, from the naming module. `buildFlickerPreview`
// normalises its window to one of the three choices before returning it, so the
// lookup always hits; the fallback is here so a future fourth window with no
// copy yet degrades to a label rather than to "undefined".
const strengthCopy = (frameWindow) =>
  DEFLICKER_STRENGTHS[frameWindow] || DEFLICKER_STRENGTHS[DEFLICKER_WINDOW_DEFAULT];

// The opening sentence of the residual caption, shared by both variants below.
// It is the answer to the question the reader actually arrived with — what does
// this operation do — and it must not drift between the one-line and two-line
// charts, so it is written once.
const WHAT_FIX_FLICKER_DOES = `${DEFLICKER_NAME} evens out sudden brightness `
  + 'jumps between frames — the strobing look time-lapses get when exposure '
  + 'shifts shot to shot — while keeping real changes in light, like a sunset.';

const Axis = () => (
  <g className="fp-axis" aria-hidden="true">
    <line x1="0" y1="0.5" x2={W} y2="0.5" />
    <line x1="0" y1={H / 2} x2={W} y2={H / 2} />
    <line x1="0" y1={H - 0.5} x2={W} y2={H - 0.5} />
  </g>
);

const Swatch = ({ variant, children }) => (
  <span className="fp-legend__item">
    <span className={`fp-legend__swatch fp-legend__swatch--${variant}`} aria-hidden="true" />
    {children}
  </span>
);

// Wording that has to survive a copy edit: the reader is told this is computed,
// what it is computed from, and that a render is a different thing.
const Disclaimer = ({ children }) => (
  <p className="fp-note" data-testid="flicker-preview-disclaimer">{children}</p>
);

// One string per reason buildFlickerPreview() can refuse to draw. Keyed rather
// than nested ternaries because the three refusals mean opposite things: a
// long, coarsely-recorded shoot told "too short" is the worst of them.
const UNAVAILABLE_COPY = {
  'no-track': 'No brightness measurement was saved for this footage, so there is nothing to graph. Projects analyzed before Tweedia started recording brightness will show this until they are analyzed again — Fix flicker still works, it just cannot be previewed here.',
  'too-short': 'This footage is too short to graph a brightness trend. Fix flicker still works; there just are not enough frames to show a before and after.',
  'too-coarse': 'Tweedia could not confirm this shoot’s brightness was recorded frame by frame, and the frame-by-frame flicker measurement that would take the comparison’s place is missing, so there is nothing here that can be graphed honestly. Fix flicker still works; only this preview is unavailable.',
};

const UNAVAILABLE_FALLBACK = 'This shoot’s brightness measurement cannot be graphed. Fix flicker still works; only this preview is unavailable.';

// `frameWindow` is the Strength the user picked (the draft's `deflickerWindow`).
// It only ever changes the AFTER line — the measured line, the axis and the 1%
// reference are defined by a fixed measurement window and stay put, so the two
// positions of the control are directly comparable on screen. Unrecognised or
// absent falls back to the default inside buildFlickerPreview.
const FlickerPreview = ({ luminanceTrack, frameWindow }) => {
  const preview = useMemo(
    () => buildFlickerPreview(luminanceTrack, frameWindow),
    [luminanceTrack, frameWindow]
  );
  // The window that was actually APPLIED, which is the prop normalised: an
  // unrecognised value falls back to the default rather than blanking the
  // chart, and every string below must quote what was drawn, not what was asked
  // for.
  const appliedWindow = preview.frameWindow;
  const strength = strengthCopy(appliedWindow);

  if (preview.mode === 'unavailable') {
    return (
      <div className="fp" data-testid="flicker-preview" data-mode="unavailable">
        <p className="fp-empty" data-testid="flicker-preview-empty">
          {UNAVAILABLE_COPY[preview.reason] || UNAVAILABLE_FALLBACK}
        </p>
      </div>
    );
  }

  if (preview.mode === 'residual') {
    const { flickerPercent, flickerPercentAfter, domain } = preview;
    const line = toPolylinePoints(flickerPercent, domain, W, H);
    const area = `0,${H} ${line} ${W},${H}`;
    const thresholdY = H * (1 - FLICKER_DETECT_THRESHOLD_PERCENT / (domain.max || 1));
    const peak = flickerPercent.reduce((m, v) => (v > m ? v : m), 0);
    // The analyzer's estimate of the flicker LEFT after the fix, measured at
    // full rate before the track was downsampled for storage. Absent on every
    // project analyzed before #405, and this component must render those
    // exactly as it always has — one line, and copy that never says "after".
    const afterLine = flickerPercentAfter
      ? toPolylinePoints(flickerPercentAfter, domain, W, H)
      : null;
    const afterPeak = flickerPercentAfter
      ? flickerPercentAfter.reduce((m, v) => (v > m ? v : m), 0)
      : null;
    // The chart's aria-label names the strength for the same reason the legend
    // does: a screen-reader user moving the control has no other way to tell
    // that the peak they just heard belongs to the tile they just picked, and
    // that peak is the entire feedback the control gives.
    return (
      <div className="fp" data-testid="flicker-preview" data-mode="residual">
        <div className="fp-head">
          <h5 className="fp-title">Flicker measured across this shoot</h5>
          <span className="fp-legend">
            <Swatch variant="residual">Brightness swing</Swatch>
            {afterLine && (
              <Swatch variant="residual-after">{afterSeriesLabel(strength.label)}</Swatch>
            )}
            <Swatch variant="threshold">Noticeable at {FLICKER_DETECT_THRESHOLD_PERCENT}%</Swatch>
          </span>
        </div>
        <svg
          className="fp-chart"
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          role="img"
          data-testid="flicker-preview-chart"
          aria-label={afterLine
            ? `Flicker measured across the shoot, peaking at ${peak.toFixed(1)} percent frame-to-frame brightness swing, and a second line for the ${afterPeak.toFixed(1)} percent peak estimated to be left after ${DEFLICKER_NAME} at ${strength.label} strength, averaging ${appliedWindow} frames. The estimate is calculated from the analysis, not a rendered preview. Anything above ${FLICKER_DETECT_THRESHOLD_PERCENT} percent is noticeable.`
            : `Flicker measured across the shoot, peaking at ${peak.toFixed(1)} percent frame-to-frame brightness swing. Anything above ${FLICKER_DETECT_THRESHOLD_PERCENT} percent is noticeable.`}
        >
          <Axis />
          <polygon className="fp-line__fill" points={area} />
          <polyline
            className="fp-line fp-line--residual"
            points={line}
            vectorEffect="non-scaling-stroke"
            data-testid="flicker-line-residual"
          />
          {/* Drawn after the measured line so the estimate stays legible where
              the two meet near zero — the same stacking as the comparison
              chart, where the smooth curve sits on top of the jagged one. */}
          {afterLine && (
            <polyline
              className="fp-line fp-line--residual-after"
              points={afterLine}
              vectorEffect="non-scaling-stroke"
              data-testid="flicker-line-residual-after"
            />
          )}
          <line
            className="fp-threshold"
            x1="0"
            y1={thresholdY}
            x2={W}
            y2={thresholdY}
            vectorEffect="non-scaling-stroke"
            data-testid="flicker-threshold-line"
          />
        </svg>
        <div className="fp-scale" aria-hidden="true">
          <span>Start of shoot</span>
          <span>{domain.max.toFixed(1)}% peak scale</span>
          <span>End</span>
        </div>
        {/* Two captions, and the fork is about what is ON THE CHART, not about
            how the footage was sampled (the storage lecture this replaced).
            With one line the reader must be told it is the PROBLEM, or they
            read the measured flicker as the outcome. With two, that warning
            becomes wrong — so the second line is named, and named as an
            ESTIMATE from the analysis. Neither string may imply a rendered A/B. */}
        {afterLine ? (
          <Disclaimer>
            {WHAT_FIX_FLICKER_DOES} The Brightness swing line is that flicker as
            measured, frame by frame; the second line is how much of it is
            expected to be left after {DEFLICKER_NAME} runs at {strength.label}{' '}
            strength — an estimate calculated from the same analysis, not a
            rendered preview, and it applies to your next render rather than to
            a video you have already made.
          </Disclaimer>
        ) : (
          <Disclaimer>
            {WHAT_FIX_FLICKER_DOES} The line above is that flicker itself,
            measured frame by frame: the problem, not the result. Calculated
            from the analysis, not a rendered preview.
          </Disclaimer>
        )}
      </div>
    );
  }

  const { before, after, domain } = preview;
  const beforePoints = toPolylinePoints(before, domain, W, H);
  const afterPoints = toPolylinePoints(after, domain, W, H);

  return (
    <div className="fp" data-testid="flicker-preview" data-mode="comparison">
      <div className="fp-head">
        <h5 className="fp-title">Predicted brightness, before and after</h5>
        <span className="fp-legend">
          <Swatch variant="before">As shot</Swatch>
          <Swatch variant="after">With {DEFLICKER_NAME} — {strength.label}</Swatch>
        </span>
      </div>
      <svg
        className="fp-chart"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        role="img"
        data-testid="flicker-preview-chart"
        aria-label={`Frame brightness across the shoot as shot, and the smoother curve predicted after Fix flicker at ${strength.label} strength averages every ${appliedWindow} frames.`}
      >
        <Axis />
        <polyline
          className="fp-line fp-line--before"
          points={beforePoints}
          vectorEffect="non-scaling-stroke"
          data-testid="flicker-line-before"
        />
        <polyline
          className="fp-line fp-line--after"
          points={afterPoints}
          vectorEffect="non-scaling-stroke"
          data-testid="flicker-line-after"
        />
      </svg>
      <div className="fp-scale" aria-hidden="true">
        <span>Start of shoot</span>
        <span>Frame brightness</span>
        <span>End</span>
      </div>
      <Disclaimer>
        Calculated from the brightness measured during analysis — not a rendered
        preview, and not a frame-by-frame comparison of the picture. At{' '}
        {strength.label} strength, Fix flicker replaces each frame&apos;s
        brightness with the average of the {appliedWindow} frames around it, so
        short jitter flattens while slow changes like a sunset stay. Every frame
        of this shoot was measured, so the jagged line is the real thing. The
        strength applies to your next render, not to a video you have already
        made.
      </Disclaimer>
    </div>
  );
};

export default FlickerPreview;
